import React, { useContext } from "react";
import {StateContext} from "../../../../components/context/StateProvider";
import Utils from "../../../../utils/Utils";
import {ESTADO_PROCESO, FORMAT_LOCALE_DIGIT, LOCALE} from "../../../../config/constants";
import QRCode from "react-qr-code";

const nombrePreparador = (preparador) =>
    preparador ? Utils.toCapitalize(preparador) : ESTADO_PROCESO.SIN_PREPARADOR;

/**
 * Fila de producto del ticket imprimible
 */
const OrderPrintProduct = ({ product }) => {

    const { getNombreAlmacen } = useContext(StateContext);

    // Formatear stock disponible
    const stockText = Array.isArray(product.stock) && product.stock.length > 0
        ? product.stock.map(item => `${getNombreAlmacen(item.ALMACEN)}: ${item.FINAL}`).join(', ')
        : 'No disponible';

    return (
        <tr>
            <td>
                <div><strong>Ref:</strong> {product.ref}</div>
                <div><strong>C. barras:</strong> {product.ean.map(item => item.BARRAS + '-' + item.UNIDADES).join(', ')}</div>
                <div><strong>Desc:</strong> {product.name}</div>
                <div><strong>Unids:</strong> {product.units}</div>
                <div><strong>Ubic:</strong> {product.location}</div>
                <div><strong>Stock:</strong> {stockText}</div>
            </td>
        </tr>
    );
};

/**
 * Ticket imprimible del albarán, oculto hasta que se lanza la impresión.
 * Por defecto imprime el pedido y los productos del contexto (el punteo abierto);
 * `order` y `products` por props permiten imprimir otro sin cargarlo en el contexto,
 * como la etiqueta de un grupo desde el listado (BotonEtiquetaGrupoN2).
 * `aviso` añade una línea destacada bajo la cabecera, para un ticket parcial
 * (la etiqueta de altura de TicketsPreparacion) que no debe confundirse con el pedido completo.
 *
 * forwardRef NO es opcional: InfoOrder se lo pasa a react-to-print para localizar
 * el nodo a imprimir. Sin él, la ref llegaría vacía y el ticket no saldría.
 * La ref apunta al div del ticket, que solo existe si hay productos — igual que
 * antes, cuando era una clase y el fragmento vacío tampoco daba nodo.
 */
const PrintOrder = React.forwardRef((props, ref) => {

    const {state} = useContext(StateContext);
    const order = props.order ?? state.order;
    const products = props.products ?? state.products;

    let qrCode = order.num+'?tipo='+order.tipo+'&ruta='+order.route;

    // Solo lo rellenan las páginas de Nave 2 (OrderN2 y OrderN2Grupo). En un grupo hay un
    // preparador de nave 1 por pedido, así que se lista cada uno con su número
    const preparadoresN1 = order.preparadoresN1 || [];
    const esGrupo = order.tipo === 'grupo';
    return <>
        {products.length > 0 ?
            <div id="print-ticket" className="hidden m-4" ref={ref}>
                <div className='row'>
                    <div className="mb-2 col-12 d-inline-flex align-items-center">
                        <div className="form-label mb-1 me-2 text-black"><h2>Albarán: {order.num}</h2></div>
                        <div className="form-text mt-0 mb-1 text-black"><QRCode value={qrCode} size={70}/></div>
                    </div>
                </div>
                {props.aviso && (
                    <div className='row'>
                        <div className="p-2 col-12 border-top-black text-black"><strong>{props.aviso}</strong></div>
                    </div>
                )}
                <div className='row'>
                    <div className="p-2 col-12 d-inline-flex align-items-center border-top-black">
                        <div className="form-label mb-1 me-2 text-black"><strong>Preparador:</strong></div>
                        <div className="form-text mt-0 mb-1 text-black">{Utils.toCapitalize(order.preparer)}</div>
                    </div>
                </div>
                {preparadoresN1.length > 0 && (
                    <div className='row'>
                        <div className="p-2 col-12 d-inline-flex align-items-start border-top-black">
                            <div className="form-label mb-1 me-2 text-black"><strong>Preparador nave 1:</strong></div>
                            <div className="form-text mt-0 mb-1 text-black">
                                {esGrupo
                                    ? preparadoresN1.map(({ pedido, preparador }) => (
                                        <div key={pedido}>{pedido}: {nombrePreparador(preparador)}</div>
                                    ))
                                    : nombrePreparador(preparadoresN1[0].preparador)}
                            </div>
                        </div>
                    </div>
                )}
                <div className='row'>
                    <div className="p-2 col-12 d-inline-flex align-items-center border-top-black">
                        <div className="form-label mb-1 me-2 text-black"><strong>Ruta:</strong></div>
                        <div className="form-text mt-0 mb-1 text-black">{Utils.toCapitalize(order.route)}</div>
                    </div>
                </div>
                <div className='row'>
                    <div className="p-2 col-12 d-inline-flex align-items-center border-top-black">
                        <div className="form-label mb-1 me-2 text-black"><strong>Fecha:</strong></div>
                        <div className="form-text mt-0 mb-1 text-black">{new Date().toLocaleString(LOCALE, {
                            day: FORMAT_LOCALE_DIGIT,
                            month: FORMAT_LOCALE_DIGIT,
                            year: FORMAT_LOCALE_DIGIT,
                            hour: FORMAT_LOCALE_DIGIT,
                            minute: FORMAT_LOCALE_DIGIT,
                            second: FORMAT_LOCALE_DIGIT,
                            hour12: false
                        })}</div>
                    </div>
                </div>
                <div className='row'>
                    <table className="table mb-0 text-black">
                        <thead>
                        <tr>
                            <th><strong>Pedido</strong></th>
                        </tr>
                        </thead>
                        <tbody>
                        {products.map(function (product, index) {
                            return (
                                <OrderPrintProduct key={index} product={product}/>
                            )
                        })}
                        </tbody>
                    </table>
                </div>
            </div>
            : ''}
    </>;
});

PrintOrder.displayName = "PrintOrder";

export default PrintOrder;
